import React, { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import {
    Menu,
    LayoutDashboard,
    Banknote,
    TrendingUp,
    Car,
    Shield,
    Landmark,
    Wrench,
    FileCheck,
    Search,
    FileText,
    LogOut,
} from 'lucide-react';

// ListItem for the NavigationMenu dropdown content
const ListItem = React.forwardRef<
    React.ElementRef<'button'>,
    React.ComponentPropsWithoutRef<'button'> & {
        title: string;
        icon?: React.ReactNode;
        badge?: number;
    }
>(({ className, title, children, icon, badge, ...props }, ref) => {
    return (
        <li>
            <NavigationMenuLink asChild>
                <button
                    ref={ref}
                    className={cn(
                        'block w-full text-left select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground',
                        className
                    )}
                    {...props}
                >
                    <div className="flex items-center gap-2 text-sm font-medium leading-none">
                        {icon}
                        {title}
                        <NavBadge count={badge} />
                    </div>
                    {children && (
                        <p className="line-clamp-2 text-xs leading-snug text-muted-foreground mt-1">
                            {children}
                        </p>
                    )}
                </button>
            </NavigationMenuLink>
        </li>
    );
});
ListItem.displayName = 'ListItem';

export interface SectionItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    description?: string;
    /**
     * A count to show beside the label. Static here (nothing sets it in SECTION_GROUPS
     * today); the live value arrives through the `badges` prop, which is what lets a page
     * feed it from a query without SECTION_GROUPS becoming stateful.
     */
    badge?: number;
}

/** Per-section counts, keyed by `SectionItem.id`. Overrides the static `badge`. */
export type SectionBadges = Record<string, number | undefined>;

/** A small filled count. Renders nothing at zero — an empty badge is noise. */
function NavBadge({ count }: { count?: number }) {
    if (!count || count <= 0) return null;
    return (
        <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-foreground px-1.5 text-[10px] font-bold tabular-nums text-background">
            {count > 9 ? '9+' : count}
        </span>
    );
}

export interface SectionGroup {
    title: string;
    items: SectionItem[];
}

const SECTION_GROUPS: SectionGroup[] = [
    {
        title: 'Overview',
        items: [
            { id: 'summary', label: 'Summary', icon: <LayoutDashboard className="h-4 w-4" />, description: 'Contract overview and value breakdown' },
        ],
    },
    {
        title: 'Financial',
        items: [
            { id: 'payments', label: 'Payments', icon: <Banknote className="h-4 w-4" />, description: 'Weekly payment history' },
            { id: 'interest', label: 'Interest', icon: <TrendingUp className="h-4 w-4" />, description: 'Overdue interest breakdown' },
            { id: 'loans', label: 'Advances', icon: <Landmark className="h-4 w-4" />, description: 'Active advances and repayment progress' },
        ],
    },
    {
        title: 'Vehicle',
        items: [
            { id: 'vehicle', label: 'Vehicle', icon: <Car className="h-4 w-4" />, description: 'Vehicle allocation and schedule' },
            { id: 'maintenance', label: 'Maintenance', icon: <Wrench className="h-4 w-4" />, description: 'Service and repair records' },
            { id: 'inspections', label: 'Inspections', icon: <Search className="h-4 w-4" />, description: 'Your monthly inspection and its record' },
            { id: 'roadworthiness', label: 'Roadworthiness', icon: <FileCheck className="h-4 w-4" />, description: 'Roadworthy certificate status' },
        ],
    },
    {
        title: 'Compliance',
        items: [
            { id: 'insurance', label: 'Insurance', icon: <Shield className="h-4 w-4" />, description: 'Insurance policies and repayment' },
            { id: 'documents', label: 'Documents', icon: <FileText className="h-4 w-4" />, description: 'Contracts and agreements' },
        ],
    },
];

interface SectionDrawerProps {
    activeSection: string;
    onSectionChange: (section: string) => void;
    onSignOut?: () => void;
    badges?: SectionBadges;
}

// Desktop NavigationMenu component (to be placed in header)
export function DesktopNav({ activeSection, onSectionChange, badges }: { activeSection: string; onSectionChange: (id: string) => void; badges?: SectionBadges }) {
    const isGroupActive = (group: SectionGroup) =>
        group.items.some(item => item.id === activeSection);
    const groupBadgeTotal = (group: SectionGroup) =>
        group.items.reduce((sum, item) => sum + (badges?.[item.id] ?? item.badge ?? 0), 0);

    return (
        <NavigationMenu>
            <NavigationMenuList>
                {SECTION_GROUPS.map((group) => (
                    <NavigationMenuItem key={group.title}>
                        <NavigationMenuTrigger
                            className={cn(isGroupActive(group) && 'bg-accent')}
                        >
                            {group.title}
                            <NavBadge count={groupBadgeTotal(group)} />
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                            <ul className="grid w-[300px] gap-2 p-3">
                                {group.items.map((item) => (
                                    <ListItem
                                        key={item.id}
                                        title={item.label}
                                        icon={item.icon}
                                        badge={badges?.[item.id] ?? item.badge}
                                        onClick={() => onSectionChange(item.id)}
                                        className={cn(
                                            activeSection === item.id && 'bg-accent'
                                        )}
                                    >
                                        {item.description}
                                    </ListItem>
                                ))}
                            </ul>
                        </NavigationMenuContent>
                    </NavigationMenuItem>
                ))}
            </NavigationMenuList>
        </NavigationMenu>
    );
}

// Mobile drawer (hamburger menu)
export default function SectionDrawer({ activeSection, onSectionChange, onSignOut, badges }: SectionDrawerProps) {
    const [open, setOpen] = useState(false);

    const handleSelect = (sectionId: string) => {
        onSectionChange(sectionId);
        setOpen(false);
    };

    const activeSectionItem = SECTION_GROUPS.flatMap(g => g.items).find(i => i.id === activeSection);

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-2 text-sm font-medium"
                >
                    <Menu className="h-5 w-5" />
                    <span className="truncate max-w-[200px]">
                        {activeSectionItem?.label || 'Summary'}
                    </span>
                </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] px-0 flex flex-col">
                <SheetHeader className="sr-only">
                    <SheetTitle>Navigation</SheetTitle>
                </SheetHeader>
                <div className="overflow-y-auto flex-1">
                    {SECTION_GROUPS.map((group, groupIdx) => (
                        <div key={group.title}>
                            {groupIdx > 0 && <Separator className="my-2" />}
                            <div className="px-4 py-2">
                                <span className="text-xs font-semibold uppercase text-muted-foreground tracking-wider">
                                    {group.title}
                                </span>
                            </div>
                            <nav className="flex flex-col gap-0.5 px-2">
                                {group.items.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => handleSelect(item.id)}
                                        className={cn(
                                            'flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors',
                                            'hover:bg-accent hover:text-accent-foreground',
                                            'active:scale-[0.98]',
                                            activeSection === item.id
                                                ? 'bg-primary/10 text-primary'
                                                : 'text-foreground/70'
                                        )}
                                    >
                                        <span className={cn(
                                            activeSection === item.id ? 'text-primary' : 'text-muted-foreground'
                                        )}>
                                            {item.icon}
                                        </span>
                                        {item.label}
                                        <NavBadge count={badges?.[item.id] ?? item.badge} />
                                    </button>
                                ))}
                            </nav>
                        </div>
                    ))}
                </div>

                {/* Sign Out */}
                {onSignOut && (
                    <div className="mt-auto border-t px-2 py-4">
                        <button
                            onClick={onSignOut}
                            className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 active:scale-[0.98]"
                        >
                            <LogOut className="h-5 w-5" />
                            Sign Out
                        </button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

export { SECTION_GROUPS };
